import { FormikProvider, useField, useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Select } from '@treams/treams-ui';

import FilledInBy from './common/FilledInBy';
import Paragraph from './common/Paragraph';
import Property from './common/Property';

function CompetencyLevelSet() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [field, , { setValue }] = useField<graphql.CompetencyLevelSet | undefined>(
		'competencyLevelSet',
	);

	const { data } = graphql.useCompetencyLevelSetsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const options =
		data?.competencyLevelSets.edges.map((edge) => ({
			value: edge.node.id,
			label: localize(edge.node.title).text,
		})) ?? [];

	return (
		<Property
			title={t('common.settings.evaluationForms.properties.finalScore.competencyLevelSet.label')}
		>
			<Select
				options={options.map(({ value, label }) => ({
					title: label,
					value,
				}))}
				selectedValue={field.value?.id}
				onChange={(value) => {
					const set = data?.competencyLevelSets.edges.find((edge) => edge.node.id === value);
					setValue(set?.node as graphql.CompetencyLevelSet);
				}}
			/>
		</Property>
	);
}

export default function ReviewGoalsModal(props: {
	fillInBy: graphql.EvaluationFillInBy;
	item: graphql.EvaluationReviewGoalsItem;
	onChange: (item: graphql.EvaluationReviewGoalsItem, fillInBy: graphql.EvaluationFillInBy) => void;
}) {
	const { t } = useTranslation();

	const formik = useFormik({
		initialValues: {
			competencyLevelSet: props.item.competencyLevelSet,
			defaultExplanation: props.item.defaultExplanation ?? [],
			fillInBy: props.fillInBy,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		if (!formik.values.competencyLevelSet) {
			return;
		}

		// An untouched Quill editor still holds a trailing newline, which would
		// otherwise be stored and pre-fill every goal with blank lines.
		const defaultExplanation = formik.values.defaultExplanation.filter(
			({ text }) =>
				text.ops
					.map((op) => op.insert ?? '')
					.join('')
					.trim().length > 0,
		);

		props.onChange(
			{
				...props.item,
				competencyLevelSet: formik.values.competencyLevelSet,
				defaultExplanation: defaultExplanation.length > 0 ? defaultExplanation : null,
			},
			formik.values.fillInBy,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values.fillInBy,
		formik.values.competencyLevelSet?.id,
		formik.values.defaultExplanation,
	]);

	return (
		<FormikProvider value={formik}>
			<Flex p={3} flexDirection="column">
				<FilledInBy />
				<CompetencyLevelSet />
				<Paragraph
					label={t(
						'common.settings.evaluationForms.properties.reviewGoals.defaultExplanation.label',
					)}
					name="defaultExplanation"
				/>
			</Flex>
		</FormikProvider>
	);
}
